import apiClient from './client';
import { msalInstance, loginRequest } from '../auth/authConfig';

export const fetchAgentConversation = async (caseNumber) => {
  const { data } = await apiClient.get(`/api/cases/${caseNumber}/agent/conversation`);
  return data;
};

export const fetchCaseBrief = async (caseNumber) => {
  const { data } = await apiClient.get(`/api/cases/${caseNumber}/agent/brief`);
  return data; // { case_number, markdown }
};

/**
 * Stream an agent chat reply over SSE.
 *
 * Calls `onDelta(text)` for each token chunk. Resolves when the stream ends.
 * Rejects on network / HTTP / server-side error frames.
 *
 * Uses fetch directly (not axios) so we get access to the ReadableStream body.
 */
export const streamAgentChat = async (caseNumber, { message, brief, history }, { onDelta, signal }) => {
  // Mirror the axios interceptor: attach bearer token via MSAL
  let authHeader = {};
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    try {
      const res = await msalInstance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      authHeader = { Authorization: `Bearer ${res.accessToken}` };
    } catch {
      // fall through — backend will 401 and we'll surface that
    }
  }

  const base = process.env.REACT_APP_API_URL || '';
  const response = await fetch(`${base}/api/cases/${caseNumber}/agent/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify({ message, brief, history }),
    signal,
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try { const j = await response.json(); detail = j.detail || detail; } catch {}
    throw new Error(detail);
  }
  if (!response.body) throw new Error('Streaming not supported by this browser');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by blank lines
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);

      // A frame is one or more lines; we only care about `data:` payload lines
      const dataLine = frame
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart())
        .join('');
      if (!dataLine) continue;

      let payload;
      try { payload = JSON.parse(dataLine); }
      catch { continue; }

      if (payload.error) throw new Error(payload.error);
      if (payload.done) return;
      if (payload.delta) onDelta(payload.delta);
    }
  }
};
